import React from 'react';
import { Grid, TextField, MenuItem, Paper, Typography, Box, Chip, LinearProgress, Stack } from '@mui/material';
import { Student } from '@/types';
import { ATTENDANCE_STATUS } from '../../StudentForm/constants';
import { format } from 'date-fns';

interface AttendanceSectionProps {
  values: Partial<Student>;
  onChange: (id: keyof Student, value: any) => void;
}

export const AttendanceSection: React.FC<AttendanceSectionProps> = ({
  values,
  onChange
}) => {
  const handleChange = (field: keyof Student) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'number' 
      ? parseInt(event.target.value) || 0
      : event.target.value;
    onChange(field, value);
  };

  const getAttendanceColor = (rate: number) => {
    if (rate >= 95) return 'success';
    if (rate >= 90) return 'warning';
    return 'error';
  };

  return (
    <Grid container spacing={3}>
      {/* Attendance Summary Card */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Attendance Summary
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Overall Attendance Rate
                  </Typography>
                  <Typography variant="subtitle2">
                    {values.attendanceRate || 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={values.attendanceRate || 0}
                  color={getAttendanceColor(values.attendanceRate || 0)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Days Present
                  </Typography>
                  <Typography variant="h4">
                    {values.daysPresent || 0}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Days Absent
                  </Typography>
                  <Typography variant="h4">
                    {values.daysAbsent || 0}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tardies
                  </Typography>
                  <Typography variant="h4">
                    {values.tardinessCount || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Last Attendance
                  </Typography>
                  <Typography>
                    {values.lastAttendanceDate ? 
                      format(new Date(values.lastAttendanceDate), 'MMMM d, yyyy') :
                      'No record'
                    }
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Current Status
                  </Typography>
                  <Chip
                    label={values.attendanceStatus?.replace('-', ' ') || 'Active'}
                    color={values.attendanceStatus === 'chronic-absence' ? 'error' : 'success'}
                    size="small"
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Form Fields */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          select
          label="Attendance Status"
          value={values.attendanceStatus || ''}
          onChange={handleChange('attendanceStatus')}
          required
        >
          {ATTENDANCE_STATUS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Attendance Rate (%)"
          value={values.attendanceRate || ''}
          onChange={handleChange('attendanceRate')}
          inputProps={{ min: 0, max: 100 }}
          required
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="date"
          label="Last Attendance Date"
          value={values.lastAttendanceDate || ''}
          onChange={handleChange('lastAttendanceDate')}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Days Absent"
          value={values.daysAbsent || ''}
          onChange={handleChange('daysAbsent')}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Days Present"
          value={values.daysPresent || ''}
          onChange={handleChange('daysPresent')}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Tardiness Count"
          value={values.tardinessCount || ''}
          onChange={handleChange('tardinessCount')}
          inputProps={{ min: 0 }}
        />
      </Grid>
    </Grid>
  );
};